import { FocusedInput } from '@datepicker-react/styled';
import { Interpolation, Theme } from '@emotion/react';
import {
  DemographicsOption,
  Option,
} from 'components/shared/CustomSelectSearch';
import { FunctionComponent, ReactElement } from 'react';
import { CaptionElement } from 'types/slate-custom-types';
import {
  ApexChartType,
  CalendarFilter,
  CollectionOptionEnum,
  CollectionType,
  DatePeriodFilter,
  DurationFilter,
  FavoriteEnum,
  GeneratedChartStatus,
  Layers,
  MediaProvider,
  MediaType,
  NotificationFilter,
  NotificationType,
  Ratio,
  SemanticSortField,
  SemanticSearchMethod,
  SortField,
  TrendingLevel,
} from 'utils/enum';
import { Peaks } from 'wavesurfer.js/types/backend';
import { IKeyItem } from '.';
import {
  ClipStatus,
  ClipStatusCode,
  HistoryTypes,
  Social,
  VideoStatusCode,
  VideoStatusText,
} from './../enum';
import { IMention } from './layers.model';

export interface ILibraryItem {
  filename: string;
  title: string;
  path: string;
  modifieddatetime: string | Date;
  size: string;
  mediaid: string;
  length: string;
  mediacontenttype: string;
  statuscode: VideoStatusCode;
  status: VideoStatusText | string;
  editor: string;
  thumbnail: string;
  publishcount: number | string;
  words: string;
  speakers: string;
  summary: string;
  isSelected?: boolean;
  createddatetime: string | Date;
  /** Omny (etc.) original publish date display string; empty when unavailable */
  publisheddatetime?: string | Date;
  video_thumbnail: string | null;
  mediatype?: string;
  userid?: string;
  noTranscript?: boolean;
  viewed?: number;
  clips_count?: number;
  provider?: MediaProvider;
  is_sensitive?: boolean;
  sensitive_items?: string;
  highlight?: {
    kind: string;
    snippet: string;
    spans: { start: number; end: number }[];
  };
  mention_count?: number | null;
  timing_precision?: string;
  resultKey?: string;
  play_start_ms?: number;
  match_end_ms?: number;
  chunk_end_ms?: number;
}

export interface IClips {
  datetime: string | Date;
  starttime: number;
  endtime: number;
  isSelected?: boolean;
  length: string;
  status: ClipStatus;
  statuscode: ClipStatusCode;
  thumbnail: string;
  versioncount: string;
  versionname: string;
  title?: string;
  from_suggested?: boolean;
  video_thumbnail?: string | null;
  hasBurnIn?: boolean;
  mediatype?: string;
  subtitleurl?: string;
  url?: string;
  share_url?: string | null;
  options?: SharingOption;
  viewed?: number;
  hasButtons: boolean;
  provider?: MediaProvider;
  updated_at?: number;
  created_at?: number;
  publishpath?: string;
  media_id?: string;
  createddatetime?: string;
  modifieddatetime?: string;
  is_topic_clip?: boolean;
}

export interface HistoryItem {
  mediaid: string;
  filename: string;
  videoLength: string;
  paragraphMode?: boolean;
  editMode?: boolean;
}

export interface IMediaDetail {
  url: string;
  webvtt: IWebVtt[];
  path: string;
  metadata: IMetadata;
  subtitleurl: string;
  history: IHistory[];
  versionname: string;
  jsonfileurl?: string;
  json_v2_url?: string;
  statuscode?: VideoStatusCode;
  waveform?: Waveform;
  customTerms?: LayerCustomTerm[];
  dar?: string;
  itemLengthMs?: number;
  provider?: MediaProvider;
}

export interface LayerCustomTerm {
  listId: string;
  listName: string;
  active: boolean;
  matched: IKeyItem[];
}

export interface Waveform {
  bits: number;
  channels: number;
  data: Peaks | number[];
  length: number;
  sample_rate: number;
  samples_per_pixel: number;
  version: number;
}

export interface IHistory {
  mediaid: string;
  type: HistoryTypes;
  datetime: string;
  versioncount: string;
}

export interface IMetadata {
  formatversion: string;
  accountid: string;
  status: string;
  tenantid: string;
  accountname: string;
  audiourl: string;
  mediaid: string;
  datefinished: Date;
  language: string;
  filename: string;
  interactiontype: string;
  length: string;
  mediacontenttype: string;
  clipid: string;
  programid: string;
  userid: string;
  programname: string;
  datecreated: Date;
  textanalytics: string;
  tags: string;
  author: string;
  title: string;
  keywords: IKeyword[];
  topics: ITopic[];
  mediatype: string;
  summary: string;
  entities: IEntities;
  thumbnail?: string;
  haveMp3?: boolean;
  haveMp4?: boolean;
  keyterms?: boolean;
  summaries?: boolean;
  names?: boolean;
  products?: boolean;
  orgs?: boolean;
  locations?: boolean;
  nationalities?: boolean;
  download?: boolean;
  captiondownload?: boolean;
  versioncount?: string;
  watermark?: boolean;
  captionstype?: 'CLOSED' | 'OPEN';
  ratio?: number;
  chapters?: boolean;
  globalList?: boolean;
  isSubscriber?: boolean;
  tenantLogo?: string;
  noTranscript?: boolean;
  iab: boolean;
  visualplaybar: boolean;
  hasAdMarkersLayer: boolean;
  speakers: boolean;
  logo: boolean;

  // Episode Info Tab (SPP-637) — parsed CMS webhook_body/program_data metadata.
  // Backend omits any field it can't resolve; UI renders "Not available" for absent values.
  externalId?: string;
  network?: string;
  organizationName?: string;
  programCategory?: string;
  publishState?: string;
  visibility?: string;
  publishedUtc?: string;
  contentRating?: string;
  episodeType?: string;
  season?: string;
  episode?: string;
  cmsDurationSeconds?: string;
  cmsAudioFileSizeBytes?: string;
  cmsAudioUrl?: string;
  cmsSourceUrl?: string;
  cmsThumbnailUrl?: string;

  // Generic (non-CMS) file size in bytes, populated for every provider going
  // forward (see ResourceMedia.size) — fallback source for LOCAL/RSS episodes.
  size?: string;

  // Episode Info Tab — Collection membership (Ticket 637). Raw collection_id
  // membership only, independent of provider/CMS ingestion — a locally
  // uploaded or RSS episode can belong to a collection just like any other.
  // Not pre-filtered by visibility: the panel cross-references these ids
  // against the Collection UI's own visible-collection state so it never
  // re-implements that filtering.
  collectionIds?: string[];
}

export interface IEntities {
  person: IKeyword[];
  location: IKeyword[];
  product: IKeyword[];
  organisation: IKeyword[];
  nationality: IKeyword[];
}

export interface IKeyword {
  keyword: string;
  mentions: IMention[];
  relevance: number;
}

export interface ITopic {
  topicName: string;
  relevance: number;
  keywords: IKeyword[];
  subTopics?: ISubTopic[];
}

export interface ISubTopic {
  name: string;
  score: number;
}

export interface IWebVtt {
  id: number;
  start: number;
  end: number;
  text?: string;
}

export interface ICaption {
  id: number;
  type: string;
  data: ICaptionData;
  children: ICaptionChildren[];
}

export interface ICaptionData {
  speaker: string;
  start: number;
  end: number;
}

export interface IData {
  dataStart: number;
  dataEnd: number;
  confidence: number;
  isKeyword: boolean;
  entities: null | string;
}

export interface ICaptionChildren {
  data: IData;
  text: string;
}

export interface IRenameRequest {
  mediaid: string;
  title: string;
}

export interface IPagingRequest {
  page_number?: number;
  page_size?: number;
  collection_id?: string | null;
  sortBy?: SortField;
  order?: string;
}

export interface ISuggestedClip {
  snippet_text: string;
  snippet_timing: [number, number];
  title: string;
}

export interface IPublishVersion {
  mediaid: string;
  haveMp3: boolean;
  haveMp4: boolean;
  title: string;
  snippetSuggestions: ISuggestedClip[];
  versions: IClips[];
  suggestedVersions: IClips[];
  trendingVersions: IClips[];
  snippetCompleted?: boolean;
}

export interface IPublishPayload {
  mediaid: string;
  versionname: string;
  starttime: string;
  endtime: string;
  captionstype: string;
  keyterms: number;
  summaries: number;
  names: number;
  products: number;
  orgs: number;
  locations: number;
  nationalities: number;
  download: number;
  captiondownload: number;
  transcriptsearch: number;
  librarysearch: number;
  versioncount?: string;
  isSuggested?: boolean;
  speakers: number;
  chapters: number;
  globalList: number;
  includeUrlCsv: number;
  logo: number;
  isSubscriber: number;
  iab: number;
  visualplaybar: number;
  paraheader: number;
  hasAdMarkersLayer: number;
  dar: Ratio;
  topicTerm?: string;
  layer?: string;
  isTopicClip?: boolean;
}

export interface IStartEnd {
  startTime: number;
  endTime: number;
}

export interface UploadS3Response {
  url: string;
  fields: IUploadHeaders;
}

export interface IUploadHeaders {
  key: string;
  'x-amz-algorithm': string;
  'x-amz-credential': string;
  'x-amz-date': string;
  'x-amz-security-token': string;
  policy: string;
  'x-amz-signature': string;
}

export interface IUploadPayload {
  filename: string;
  mediatype: string;
  title: string;
  duration: number;
}

export interface CustomTerm {
  id: string;
  name: string;
  active: boolean;
  terms?: Term[];
  versionNo?: number;
  dateCreated?: string | Date;
  lastModified?: string | Date;
  canApply?: boolean;
  insightVisible?: boolean;
}

export interface WatchList {
  id: string;
  name: string;
  active: boolean;
  terms?: Term[];
  versionNo?: number;
  dateCreated?: string | Date;
  lastModified?: string | Date;
  recipients: Recipients;
  email?: string;
}

export interface Term {
  id: string;
  name: string;
  dateCreated: string | Date;
  alternatives?: Term[];
  type?: string;
  fromCSV?: boolean;
}

export type InsightTerm = Pick<Term, 'id' | 'name'> & {
  selected: boolean;
  isInputTerm: boolean;
};

export type InsightsTrendingCharts = {
  selectedCollectionId: string | null;
  calendarMode: CalendarFilter;
} & DateRange;

export interface CustomDateParams {
  startDate: Date | null;
  endDate: Date | null;
  focusedInput: FocusedInput | null;
  dateSelected?: Date | null;
}

export type DateRange = {
  startDate: Date | null;
  endDate: Date | null;
};

export interface PublishRequest {
  download: boolean;
  captiondownload: boolean;
  keyterms: boolean;
  summaries: boolean;
  products: boolean;
  orgs: boolean;
  locations: boolean;
  nationalities: boolean;
  people: boolean;
  hasBurnedIn: boolean;
  chapters: boolean;
  globalList: boolean;
  includeUrlCsv: boolean;
  logo: boolean;
  isSubscriber: boolean;
  iab: boolean;
  visualplaybar: boolean;
  paraheader: boolean;
  hasAdMarkersLayer: boolean;
  speakers: boolean;
}

export interface MentionReport {
  id: number;
  term: string;
  type: string;
  enabled: boolean;
  caption: CaptionElement;
  occNo: number[];
  totalOcc: number;
}

export interface CombinedClipRequest {
  mediaid: string;
  versionname: string;
  starttime: string;
  endtime: string;
  clips: {
    captions: CombinedCaption[];
  };
}

export interface CombinedCaption {
  s: number;
  e: number;
}

export interface SagaAction {
  type: string;
  [type: string]: any;
}

export interface RevivalItemModel {
  // libraryItem: ILibraryItem,
  id: string;
  libraryItem: Partial<ILibraryItem>;
  topTags: string[];
  twitterTrendingTags: string[];
  headline: string;
  trendingOn: string[];
  hasMetrics: boolean;
  mentionCount: number;
  retweetLevel: string;
  replyLevel: string;
}

export interface GroupRevival {
  id: string;
  date: Date;
  type: string;
  items: RevivalItemModel[];
}

export interface GroupNotify {
  date: string;
  items: NotifyList[];
}

export interface UpdateTitle {
  mediaid: string;
  title: string;
}

export interface NotifyList {
  id: string;
  date: string;
  mediaid: string;
  fileName?: string;
  versioncount?: string | null;
  type: NotificationType;
  filter: NotificationFilter;
  status?: VideoStatusCode | number | string;
  data?: { [type: string]: any } | null;
}

export interface RevivalPagination {
  from: number;
  size: number;
  total: number;
  data: RawRevival[];
}

export interface RawRevival {
  id: string;
  tenant_id: string;
  headline: string;
  status: 'Trending';
  created_at: Date;
  media: RawRevivalMedia;
  revive_clip?: any[];
  term_metric: TermMetric;
  top_tags: string[];
  topic_twitter_trending: string[];
  trending_mentions: RawTrendingMention[];
}

export interface TermMetric {
  count: number;
  retweet_level: TrendingLevel;
  reply_level: TrendingLevel;
}

export interface RawRevivalMedia {
  media_id: string;
  media_type?: 'video' | 'audio' | null;
  title: string;
  video_thumbnail: string;
  thumbnail: string;
}

export interface RawTrendingMention {
  provider: Social;
  mentions: string;
}
export interface PreferenceResponse {
  settings: Setting;
  customterms: CustomTerm[];
  emailsettings: RawEmailSetting;
  monetisation?: Monetisation;
  omny_integrations?: OmnyRawResponse[];
  megaphone_integrations?: MegaphoneRawResponse[];
  simplecast_integrations?: SimplecastRawResponse[];
  watch_lists?: WatchList[];
  speakers: SpeakerPayload;
  setting_ignore_terms: Term[];
  setting_alias_terms: AliasTerm[];
}

export interface RawEmailSetting {
  receive_processing: boolean | null;
  receive_revival: boolean | null;
  receive_watch_list: boolean | null;
  receive_hard_limit_alert: boolean | null;
  receive_soft_limit_alert: boolean | null;
}

export interface EmailSetting {
  hasProcessingNotification: boolean;
  hasRevivalNotification: boolean;
  hasWatchListNotification: boolean;
  hasSoftLimitAlertNotification: boolean;
  hasHardLimitAlertNotification: boolean;
}

export interface MidRollSetting {
  isMaximumSelected?: boolean;
  maximumMinutes?: number;
  gapMinutes?: number;

  isEverySelected?: boolean;
  everyMinutes?: number;
}

export interface Monetisation {
  monetisationEnabled: boolean;
  preRoll: boolean;
  midRolls: boolean;
  postRoll: boolean;
  midRollsConfigs: MidRollSetting;
  isCheckAdsNumber: boolean;
  adsNumber: number;
}

export interface Setting {
  clips: number;
  summary: boolean;
  suggest: boolean;
  caption: boolean;
  captionfile: boolean;
  transcripts: boolean;
  keyterm: boolean;
  products: boolean;
  nationalities: boolean;
  datasummary: boolean;
  orgs: boolean;
  people: boolean;
  locations: boolean;
  includeUrlCsv: boolean;
}

export interface ConfirmModalProps {
  title?: string;
  message?: string | ReactElement;
  cancelText?: string;
  confirmText?: string;
  hideCancel?: boolean;
  hideConfirm?: boolean;
  type?: 'warning' | 'info' | 'error';
  classes?: string;
}

export type ConfirmWithCheckboxModalProps = ConfirmModalProps;

export interface PaginationModalProps {
  title: string;
}

export interface CreateNameModalProps {
  title: string;
  headline: string;
  btnTitle: string;
}

export interface ShareSocialTrending {
  mediaid: string;
  shareURL: string;
  shareId: string;
  provider: Social;
}

export interface EmbedOption {
  hasSummary: boolean;
  hasTranscript: boolean;
  hasVideo: boolean;
  hasEntities: boolean;
  allowedExport: boolean;
  allowedShare: boolean;
  logoRemove: boolean;
}

export interface IABMock {
  recommendedCategories: IABItemModel[];
  allCategories: IABItemModel[];
}

export interface IABItemModel {
  id: string;
  parentId?: string;
  tier?: number;
  name: string;
  isRecommended?: boolean;
  hierarchy?: string;
  raw_hierarchy_name?: string;
  subItems?: IABItemModel[];
  relevance?: number;
  isManual?: boolean;
  chapters?: [
    {
      s: number;
      e: number;
    },
  ];
  severityLevel?: 'low severity' | 'medium severity' | 'high severity';
}

export interface HighLevelProcessingProfileState {
  iab: boolean;
  entities: boolean;
  chapterisation: boolean;
  waveform: boolean;
}

export interface TenantProcessingProfileState {
  highLevelProcessingProfile: HighLevelProcessingProfileState;
}

export const DEFAULT_HIGH_LEVEL_PROCESSING_PROFILE: HighLevelProcessingProfileState =
  {
    iab: true,
    entities: true,
    chapterisation: true,
    waveform: true,
  };

export const mergeHighLevelProcessingProfile = (
  preference?: Partial<{
    highLevelProcessingProfile?: Partial<HighLevelProcessingProfileState>;
  }>,
): HighLevelProcessingProfileState => {
  const profile = preference?.highLevelProcessingProfile;

  return {
    iab: profile?.iab ?? DEFAULT_HIGH_LEVEL_PROCESSING_PROFILE.iab,
    entities:
      profile?.entities ?? DEFAULT_HIGH_LEVEL_PROCESSING_PROFILE.entities,
    chapterisation:
      profile?.chapterisation ??
      DEFAULT_HIGH_LEVEL_PROCESSING_PROFILE.chapterisation,
    waveform: profile?.waveform ?? DEFAULT_HIGH_LEVEL_PROCESSING_PROFILE.waveform,
  };
};

export type PreferenceOption = SystemOption &
  SharingOption &
  TenantProcessingProfileState;

export interface SystemOption {
  clips: number;
  suggestedTotalShorts: number;
  suggestedTotalClips: number;
  suggestedTotalSegments: number;
  autoSaveMinutes: number;
  summary: boolean;
  caption: boolean;
  suggest: boolean;
  includeUrlCsv: boolean;
  isSubscriber: boolean;
  visualplaybar: boolean;
  autoSave: boolean;
  hasAdMarkersLayer?: boolean;
  paragraphMode: boolean;
}

export interface SharingOption {
  logo: boolean;

  captionfile: boolean;
  transcripts: boolean;
  paraheader: boolean;

  keyterm: boolean;
  products: boolean;
  nationalities: boolean;
  datasummary: boolean;
  orgs: boolean;
  people: boolean;
  locations: boolean;
  globalList: boolean;
  chapters: boolean;
  iab: boolean;
  speakers: boolean;

  visualplaybar: boolean;
  hasAdMarkersLayer?: boolean;
}

export interface APISharingOptionPayload {
  logo: boolean;
  captiondownload: boolean;
  download: boolean;
  paraheader: boolean;
  keyterms: boolean;
  products: boolean;
  nationalities: boolean;
  summaries: boolean;
  orgs: boolean;
  names: boolean;
  locations: boolean;
  globalList: boolean;
  chapters: boolean;
  iab: boolean;
  hasAdMarkersLayer: boolean;
}

type ResponseItem = {
  S: string;
};

export type SubdomainResponse = {
  DomainName: ResponseItem;
  tenantid: ResponseItem;
};

export type PublicShareOptions = {
  hasKeyTerm?: boolean;
  hasSummary?: boolean;
  hasPeople?: boolean;
  hasProducts?: boolean;
  hasOrgs?: boolean;
  hasLocations?: boolean;
  hasNationalities?: boolean;
  hasChapter?: boolean;
  hasGlobalList?: boolean;
  hasIAB?: boolean;
  hasAdMarkers?: boolean;
  hasAdBreaks?: boolean;
  hasSpeakers?: boolean;
  mediaid?: string;
  mpx?: string;
  filename?: string;
  versioncount?: string;
};

export type PublicExportOptions = {
  allowMediaDownload: boolean;
  allowTranscriptDownload: boolean;
  allowCaptionDownload: boolean;
  haveMp3: boolean;
  haveMp4: boolean;
  isBurnedIn: boolean;
};

export interface RSSResponse {
  success: boolean;
  data: Data;
  total_count: number;
  hasNext: boolean;
  currentPage: number;
}

export interface Data {
  meta: RssMeta;
  episodes: RssFeed[];
}

export interface RssFeed {
  id?: string;
  title: string;
  description: string;
  imageURL: string;
  pubDate: Date | string;
  enclosure: RssEnclosure;
  duration?: number | string;
  summary: string;
  explicit: boolean;
}

export type RssFeedDisplay = RssFeed & {
  id: string;
};

export interface OmnyFeed {
  id: string;
  title: string;
  description: string;
  imageURL: string;
  pubDate: Date | string;
  duration?: number | string;
  summary: string;
}

export interface RssEnclosure {
  length: string | null;
  type: string | null;
  url: string;
}

export interface RssMeta {
  title: string;
  description: string;
  imageURL: string;
  link: string;
  language: string;
  editor: string;
  author: string;
  summary: string;
  categories: string[];
  owner: RssOwner;
  explicit: boolean;
}

export interface RssOwner {
  email: string;
}

export interface RssSubscription {
  id: string;
  created_at: string;
  updated_at: string;
  title: string;
  rss_url: string;
  subscribed?: boolean;
  collection_id?: string;
}

export interface MentionDownload {
  file_name: string;
  start_time: number;
  end_time: number;
  duration: number;
  keyword: string;
  total_mentions: number;
  type_mention: string;
  mention_order: number[];
  transcript: string;
  media_id: string;
}

export interface RerunPayload {
  mediaid: string;
  status: string;
}

export interface CollectionSidebarItem {
  id: string;
  type: CollectionType;
  name: string;
  order?: string;
  isArchived?: boolean;
}

export interface ProgramIdOption {
  label: string;
  value: string;
  imageURL: string;
}

export type PlaylistIdOption = {
  label: string;
  value: string;
  imageURL: string;
  programId: string;
};

export interface OmnyProgramFilterResponse {
  programId: string;
  programName: string;
}

export interface OmnyFilterResponse {
  programFilters: OmnyProgramFilterResponse[];
  excludeProgramFilters?: OmnyProgramFilterResponse[];
  titleContains: string[];
  isUseProgramName: boolean;
  isAutomatePushBack: boolean;
  integrationSettings: IntegrationSettingsPayload;
}

export interface OmnyRawResponse {
  enabled_api: boolean;
  enabled_admarkers: boolean;
  admarkers_minutes: number;
  api_key: string;
  webhook_secret: string;
  collection_option: CollectionOptionEnum;
  collection_id: string;
  id: string;
  provider: string;
  integration_name: string;
  filter: OmnyFilterResponse;
}

export type MegaphoneFilterResponse = {
  programFilters: MegaphoneProgramFilterResponse[];
  isUseProgramName: boolean;
  isAutomatePushBack: boolean;
  integrationSettings: MegaphoneIntegrationSettingsPayload;
};

export type MegaphoneRawResponse = {
  api_key: string;
  collection_id: string;
  collection_option: CollectionOptionEnum;
  created_at: string;
  enable: boolean;
  enabled_api: boolean;
  filter: MegaphoneFilterResponse;
  id: string;
  integration_name: string;
  provider: string;
  updated_at: string;
  user_id: string;
};

export interface ChapterOmnyRawResponse {
  Id: string;
  Name: string;
  Position: string;
  Tags: string | null;
}

export interface MonetizationRawResponse {
  PreRoll: boolean;
  PostRoll: boolean;
  MidRolls: string[];
}
export interface PreviewRawResponse {
  audio_url: string;
  chapters: ChapterOmnyRawResponse;
  description: string;
  duration_seconds: number;
  embed_url: string;
  id: string;
  image_url: string;
  monetization: MonetizationRawResponse;
  published_url: string;
  published_utc: string;
  summary: string | null;
  tags: string[];
  title: string;
  video_url: string | null;
  visibility: string;
}

export interface ChapterOmnyRawResponse {
  Id: string;
  Name: string;
  Position: string;
  Tags: string | null;
}

export interface MonetizationRawResponse {
  PreRoll: boolean;
  PostRoll: boolean;
  MidRolls: string[];
}
export interface PreviewRawResponse {
  audio_url: string;
  chapters: ChapterOmnyRawResponse;
  description: string;
  duration_seconds: number;
  embed_url: string;
  id: string;
  image_url: string;
  monetization: MonetizationRawResponse;
  published_url: string;
  published_utc: string;
  summary: string | null;
  tags: string[];
  title: string;
  video_url: string | null;
  visibility: string;
}

export interface AdMarkerItem {
  id: string;
  second: number;
}

export interface AdmarkerLayerPayload {
  marker: string;
  totalMarkers: number;
  startTime: number;
  adsServed: number;
  iab: string;
  tags: string;
  transcriptPrior: string;
  transcriptAfter?: string;
  url: string;
}

export interface MentionLayerData {
  id: string;
  index: number;
  term: string;
  startTime: number;
  endTime: number;
  duration: number;
  totalMentions: number;
  iab: string;
  transcript: string;
  url: string;
}

export interface BakedInTextDetector {
  id: string;
  episodeId: string | null;
  episodeTitle: string | null;
  startMarker: string;
  startIncludedInAd: boolean;
  endMarker: string;
  endIncludedInAd: boolean;
}

export interface BakedInFreeTextSearch {
  query: string;
  startTimeSec: number;
  endTimeSec: number;
}

export interface BakedInProgrammaticAd {
  isEnabled: boolean;
  placement: 'before' | 'after';
  rollType: 'preroll' | 'midroll' | 'postroll';
  adsPerSlot: number;
}

export interface BakedInJingleDetectionMethod {
  isEnabled: boolean;
  jingleFileName: string;
  jingleFileUrl: string;
  jingleDetector: 'sonic' | 'music';
}

export interface BakedInStartAndEndPhrasesDetectionMethod {
  isEnabled: boolean;
  textDetectors: BakedInTextDetector[];
}

export interface BakedInFullAdTranscriptMatchDetectionMethod {
  isEnabled: boolean;
  fullText: string;
}

export interface BakedInAiPoweredDetectionMethod {
  isEnabled: boolean;
  freeTextSearch: BakedInFreeTextSearch;
}

export interface BakedInDetectionMethods {
  jingle: BakedInJingleDetectionMethod;
  startAndEndPhrases: BakedInStartAndEndPhrasesDetectionMethod;
  adTranscriptMatch: BakedInFullAdTranscriptMatchDetectionMethod;
  aiPowered: BakedInAiPoweredDetectionMethod;
}

export type BakedInDetectionMethod = keyof BakedInDetectionMethods;

export interface BakedInAdvancedOptions {
  action: 'keep' | 'remove';
  // Always holds exactly the 4 keys of BakedInDetectionMethods.
  detectionMethods: BakedInDetectionMethods;
  sponsorBrands: string[];
  programmaticAd: BakedInProgrammaticAd;
}

export interface BakedInOptions {
  countAsMidroll: boolean;
  jingleIdentification: boolean;
  placement: 'before' | 'after';
  advanced: BakedInAdvancedOptions;
}

export interface JingleOptions {
  alwaysPlaceMidroll: boolean;
  isPostrollCheck: boolean;
  isPrerollCheck: boolean;
  placement: 'before' | 'between' | 'after';
  detector: 'sonic' | 'music';
}

export interface AdsConfigOptions {
  bakedIn: number;
  other: number;
  topicChanged: boolean;
}

export interface OnmnyMidrollOptions {
  countAsMidroll: boolean;
  placement: 'anywhere' | 'before' | 'after';
}

export interface SilenceOptions {
  alwaysPlaceMidroll: boolean;
  silenceDuration: number;
}

export interface TextSearchOptions {
  terms: string[];
  placement: 'before' | 'after';
}

export type MoneCollectionConfig<T> = {
  isEnabled: boolean;
} & T;

export type JingleConfig = MoneCollectionConfig<JingleOptions>;
export type BakedInConfig = MoneCollectionConfig<BakedInOptions>;
export type MultipleAdConfig = MoneCollectionConfig<AdsConfigOptions>;
export type OmnyMidrollConfig = MoneCollectionConfig<OnmnyMidrollOptions>;
export type SilenceConfig = MoneCollectionConfig<SilenceOptions>;
export type TextSearchConfig = MoneCollectionConfig<TextSearchOptions>;

export interface CollectionMonetisation {
  bakedInConfig: BakedInConfig;
  jingleConfig: JingleConfig;
  multipleAdsConfig: MultipleAdConfig;
  omnyMidrollsConfig: OmnyMidrollConfig;
  silenceConfig: SilenceConfig;
  textSearchConfig: TextSearchConfig;
}

export interface ChapterLayerData {
  id: number;
  index: number;
  headline: string;
  tags: string;
  iabCategories: string;
  transcript: string;
  startTime: number;
  endTime: number;
  url: string;
}

export interface AdvancedSearchParams {
  collection_id: string[];
  isShowClipsOnly: boolean;
  uploadDate: DatePeriodFilter | null;
  customDate: CustomDateParams | null;
  type: MediaType | null;
  duration: DurationFilter | null;
  speakerNames: string[];
  searchValues: string[];
  pageNumber: number;
  pageSize: number;
  searchField: string | null;
  searchBy: string | null;
  matchType: string | null;
  languages: string[];
  excludeWords: string[];
}

export interface Recipients {
  me?: boolean;
  allUsers?: boolean;
  hasEmailAddress?: boolean;
  emailAddress?: string[];
}

export interface YoutubePreviewResponse {
  items: YoutubeChannelItem[];
  currentItem: number;
  totalVideo: number;
}

export interface YoutubeChannelItem {
  id: string;
  thumbnails: YoutubeThumbnail[];
  title: string;
  channel: YoutubeChannelInfo;
  duration: string;
  accessibility: ChannelAccessibility;
  link: string;
  isPlayable?: boolean;
  url: string;
}

export interface ChannelAccessibility {
  title: string;
  duration: string;
}

export interface YoutubeChannelInfo {
  name: string;
  id: string;
  link: string;
}

export interface YoutubeThumbnail {
  url: string;
  width: number;
  height: number;
}

export interface YoutubeChannelExtractor {
  channelId: string | null;
  channelName: string | null;
  isChannelId: boolean;
  isValidChannelUrl?: boolean;
}

export type SortKey =
  | 'relevance'
  | 'modified'
  | 'frequent'
  | 'alphabet'
  | 'appearance'
  | 'order'
  | 'time'
  | 'words';

export interface SortList {
  id: SortKey;
  name: string;
  hidden: boolean;
}

export interface Pagination {
  isListView: boolean;
  page_number: number;
  page_size: number;
  isLoaded: true;
  sortBy: SortField;
  isAsc: boolean;
  semanticSortBy: SemanticSortField;
  semanticIsAsc: boolean;
  semanticSearchMethod: SemanticSearchMethod;
}

export interface LibraryStatistics {
  sharedItems: number;
  totalAudio: number;
  totalDurations: number;
  totalFiles: number;
  totalVideo: number;
}
export type Insights = {
  keywords: Aggregation;
  locations: Aggregation;
  nationalities: Aggregation;
  orgs: Aggregation;
  people: Aggregation;
  products: Aggregation;
  iab: Aggregation;
  speakers: Aggregation;
  sensitive: Aggregation;
  transcripts: Aggregation;
};

export type Aggregation = {
  buckets: Bucket[];
} & AggregationMeta;

export type AggregationMeta = {
  doc_count_error_upper_bound: number;
  sum_other_doc_count: number;
};

export type Bucket = {
  key: string;
  doc_count: number;
  is_enabled: boolean;
  alias: string | null;
  relevance_score?: number;
};

export type IabBucket = {
  media_ids: IabMediaAgg;
} & Bucket;

export type IabMediaAgg = {
  buckets: IabMediaBucket[];
} & Aggregation;

export type IabMediaBucket = {
  metadata: ILibraryItem;
} & Bucket;

export type BulkUploadPayload = {
  apiKey: string | null;
  programId: string | null;
  collectionId: string | null;
  playlistId: string | null;
  playlistProgramId: string | null;
};

export interface IntegrationOptions {
  id: string;

  isEnabled: boolean;

  apiKey?: string;
  webhookSecret?: string;
  integrationName: string;

  collectionOption: CollectionOptionEnum;
  collectionId: string | null;

  isAutomatedPushBackOmny?: boolean;

  provider: string;

  filter: IntegrationFilterPayload;
}

export type MegaphoneIntegrationOption = {
  id: string;

  isEnabled: boolean;

  apiKey?: string;

  collectionOption: CollectionOptionEnum;
  collectionId: string | null;

  isAutomatedPushBackMegaphone?: boolean;

  provider: string;

  filter: MegaphoneIntegrationFilterPayload;
};

export type SimplecastProgramFilter = {
  programId: string;
  programName: string;
  rssFeedUrl?: string;
  rssValidationStatus?:
    | 'unvalidated'
    | 'validating'
    | 'valid'
    | 'invalid'
    | 'error';
  rssValidationMessage?: string;
};

export type SimplecastProgramFilterResponse = {
  program_id: string;
  programName: string;
  rss_feed?: string;
};

/** Simplecast prefs: no chapters (CMS unsupported); automate lives on filter, not here. */
export type SimplecastIntegrationSettingsPayload = {
  hasMonetisation: boolean;
  hasTags: boolean;
  hasSummary: boolean;
};

export type SimplecastFilterResponse = {
  programFilters: SimplecastProgramFilterResponse[];
  isAutomatePushBack?: boolean;
  integrationSettings?: SimplecastIntegrationSettingsPayload;
};

export type SimplecastRawResponse = {
  api_key: string;
  collection_id: string;
  collection_option: CollectionOptionEnum;
  enabled_api: boolean;
  filter: SimplecastFilterResponse;
  id: string;
  provider: string;
  rss_feed_url?: string;
};

export type SimplecastIntegrationFilterPayload = {
  programFilters: SimplecastProgramFilter[];
  isAutomatePushBack: boolean;
  integrationSettings: SimplecastIntegrationSettingsPayload;
};

export type SimplecastIntegrationOption = {
  id: string;

  isEnabled: boolean;

  apiKey?: string;

  collectionOption: CollectionOptionEnum;
  collectionId: string | null;

  provider: string;

  filter: SimplecastIntegrationFilterPayload;
};

export type MegaphoneIntegrationFilterPayload = {
  programFilters: MegaphoneProgramFilterResponse[];
  isUseProgramName: boolean;
  isAutomatePushBack: boolean;
  integrationSettings: MegaphoneIntegrationSettingsPayload;
};

export type MegaphoneProgramFilterResponse = {
  networkId: string;
  programId: string;
};

export type MegaphoneIntegrationSettingsPayload = {
  hasMonetisation: boolean;
};

export interface IntegrationFilterPayload {
  programFilters: OmnyProgramFilterResponse[];
  excludeProgramFilters: OmnyProgramFilterResponse[];
  titleContains: string[];
  isUseProgramName: boolean;
  isAutomatePushBack: boolean;
  integrationSettings: IntegrationSettingsPayload;
}

export interface IntegrationSettingsPayload {
  hasMonetisation: boolean;
  hasChapters: boolean;
  hasTags: boolean;
  hasSummary: boolean;
  isAutomatePushBack: boolean;
}

export interface ApiKey {
  id: string;
  created_at: string;
  created_by: string;
  api_key: string;
  name: string;
}

export interface SpeakerDiarisation {
  id: string;
  speakerName: string;
  hasPrompt: boolean;
  hasIdentify: boolean;
}

export interface SpeakerState {
  hasSpeakerDiarisation: boolean;
  hasSpeakerIdentification: boolean;
  recommendedSensitivity: number;
  speakers: SpeakerDiarisation[];
}

export interface SpeakerPayload {
  speaker_config: SpeakerConfigPayload;
  speaker_items: SpeakerDiarisation[];
}

export interface SpeakerConfigPayload {
  speaker_diarisation: boolean;
  speaker_diarisation_sensitivity: number;
  speaker_identification: boolean;
}

export interface CreatedReportsPayload {
  tenant_id: string;
  report_id: string;
  status: string;
  created_at: string;
  created_by: string;
  report_title: string;
  type_report: string;
}

export interface CreatedReports {
  id: string;
  title: string;
  createdAt: string;
  createdBy: string;
  status: string;
  type: string;
}
export interface AdBreakItem {
  id?: string;
  startTime: number;
  endTime: number;
}

export interface ThreeDotMenuItemProps {
  id?: FavoriteEnum;
  isHrDivider?: boolean;
  isFavorited?: boolean;
  itemCss?: Interpolation<Theme>;
  handleOnClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void | any;
  SvgComponent?: FunctionComponent<React.SVGProps<SVGSVGElement>> | any;
  itemName?: string;
  isHide?: boolean;
  isDisabledExpiredHint?: boolean;
  className?: string;
  shouldHideFavorite?: boolean;
}

export type SeriesKeyValue = {
  [key: string]: [string, number, string][];
};

export type SeriesValue = SeriesKeyValue[keyof SeriesKeyValue];

export type VocabDropdown = Option & {
  terms: Term[];
};

export type ButtonItem = {
  label: string;
  value: string;
  icon?: ReactElement;
  disabled?: boolean;
};

export type AdvertisingProfile = {
  iab: {
    buckets: IabBucket[];
  };
  collections: AdvertisingCollection[];
  ad_markers: AdMarkerAdvertising;
  iab_tags: IabTagsAdvertising;
  iab_total_relevance: {
    buckets: IabBucket[];
  };
};

export type IabTagsAdvertising = {
  high_relevance: TotalCount;
  medium_relevance: TotalCount;
  low_relevance: TotalCount;
};

export type AdMarkerAdvertising = {
  pre_rolls: TotalCount;
  mid_rolls: TotalCount;
  post_rolls: TotalCount;
};

export type AdvertisingCollection = {
  ad_markers: number;
  collection_id: string;
  collection_name: string;
};

export type TopShow = {
  showName: string;
  showId?: string;
  score: number;
};

export type TotalCount = {
  total_count: number;
};

export type YoutubeSocialPayload = {
  channel_info: ChannelInfo;
  access_token: string;
  refresh_token: string;
  created_at: string;
  updated_at: string;
  connected: boolean;
};

export type ChannelInfo = {
  title: string;
  description: string;
  customUrl: string;
  publishedAt: Date;
  thumbnails: ThumbnailVariants;
  localized: Localized;
};

export type Localized = {
  title: string;
  description: string;
};

export type ThumbnailVariants = {
  default: Thumbnail;
  medium: Thumbnail;
  high: Thumbnail;
};

export type Thumbnail = {
  url: string;
  width: number;
  height: number;
};

export type PinnedChartBackend = {
  id: string;
  title: string;

  searchInputTerm: string;
  chartType: ApexChartType;
  calendarMode: CalendarFilter;

  vocabListId: string | null;
  vocabTerms: InsightTerm[];
  inputTerms: InsightTerm[];

  showDataLabels: boolean;
  showQuarterMarkers: boolean;
  groupByCollection: boolean;

  selectedTagIds: string[];
  selectedCollectionIds: string[];
  selectedOrgIds: string[];

  selectedLayer: Layers;
  selectedMetadataLayer: Layers;
  dateRange: CustomDateParams;
};

export type CollectionDropdownOption = {
  value: string;
  label: string;
  type: CollectionType;
  __isNew__?: boolean;
};

export type SuggestedTotal = {
  inputName: string;
  number: number;
};

export type EmbedDownloadForm = {
  mediaFileMp3: boolean;
  mediaFileMp4: boolean;
  captionsFileVtt: boolean;
  captionsFileSrt: boolean;
  transcriptTimedCaptionsPdf: boolean;
  transcriptTimedCaptionsDocx: boolean;
  transcriptTimedCaptionsTxt: boolean;
  transcriptSpeakerParagraphsPdf: boolean;
  transcriptSpeakerParagraphsDocx: boolean;
  transcriptSpeakerParagraphsTxt: boolean;
};

export type ExportEmbedType = {
  media: IMediaDetail;
  exportOption: EmbedDownloadForm;
  isBurnedIn: boolean;
};

export type ExportEmbedResponse = {
  mp3?: string;
  mp4?: string;
  textData: {
    fileName?: string;
    zipUrl?: string;
    notZipUrl?: string;
  };
};

export type EmbedParams = {
  hasSummary: boolean;
  hasTranscript: boolean;
  hasPlayer: boolean;
  hasKeyTerms: boolean;
  hasExport: boolean;
  hasShare: boolean;
  hasLogo: boolean;
};

export type CollectionProperties = {
  collectionId: string;
  collectionName: string;
  createdAt: Date;
  modifiedAt: Date;
  tagItems: CollectionTag[];
  statistics: LibraryStatistics;
  vocabList: CollectionVocabList | null;
};

export type CollectionVocabList = {
  globalList: string[];
  customList: CustomTerm[];
};

export type CollectionTag = {
  tagId: string;
  tagName: string;
};

export type AliasTerm = {
  original: string;
  alias?: string | null;
  isEnabled: boolean;
};

export type ListOption = {
  label: string;
  value: string;
};

export type FilterMenuOptions = {
  tagIds: string[];
  collectionIds: string[];
  orgIds: string[];
};

export type SimilarShowsRaw = {
  collection_id: string;
  collection_name: string;
  iab: number;
  keywords: number;
  magnitude_percentage: number;
  top_5_agg1: string[];
  top_5_agg2: string[];
  number_episodes: number;
};

export type GeneratedChartRaw = {
  id: string;
  key_term: string;
  layer: string;
  status: GeneratedChartStatus;
  insight_json_path: string;
  buckets: Bucket[];

  isPlaceholder?: boolean;
  related_layer?: keyof Insights;
};

export type AvailableUser = {
  email: string;
  groupId: string;
  groupName: string;
  firstName: string;
  lastName: string;
  role: string;
  status: string;
  createdDate: string | Date;
  modifiedDate: string | Date;
  lastLogin: string | Date;
};

export type UserGroupCollection = {
  collectionId: string;
  collectionName: string;
};

export type DefaultGroupNames = 'Administrators' | 'Users';

export type GroupNames = string | DefaultGroupNames;

type UserGroupBase = {
  groupId: string;
  groupName: GroupNames;
  createdAt: Date;
  modifiedAt: Date;
};

export type UserGroupPermission = {
  isAdmin: boolean;
  isEnabledEdit: boolean;
  isEnabledPublish: boolean;
};

export type UserGroupResponse = UserGroupBase &
  UserGroupPermission & {
    isDefault: boolean;
  };

export type UserGroupByIdResponse = UserGroupBase & {
  userIds: string[];
  collections: UserGroupCollection[];
};

export type EmbedStyle = {
  id: string;
  header: string;
  description: string;
  height: number;
  hasTranscript: boolean;
  icon: ReactElement;
};

export type EmbedListLayer = {
  hasTranscript: boolean;
  hasSummary: boolean;
  hasIAB: boolean;
  hasChapter: boolean;
  hasKeyTerm: boolean;
  hasPeople: boolean;
  hasProducts: boolean;
  hasOrgs: boolean;
  hasLocations: boolean;
  hasNationalities: boolean;
};

export type PodderDemographic = {
  group_probability: number;
  podcast_name: string;
  reach: number;
};

export type DemographicsFieldName =
  | 'homeType'
  | 'buildingAge'
  | 'education'
  | 'home'
  | 'wealth'
  | 'lifeStage'
  | 'homeSize'
  | 'area'
  | 'savings'
  | 'techProficiency'
  | 'employerSize'
  | 'device'
  | 'source'
  | 'country'
  | 'gender'
  | 'iab';

export type DemographicsCategory = {
  category: string;
  fieldName: DemographicsFieldName;
  options: DemographicsOption[];
  column: number;
};

export type Demographics = {
  [key in DemographicsCategory['fieldName']]: string[];
};
